"use client";

import { type Locale } from "date-fns";
import { createContext, type ReactNode, useContext } from "react";

import {
  type CalendarBusinessHours,
  type CalendarEvent,
  type CalendarLabels,
  type CalendarRange,
  type CalendarTone,
} from "./types";
import { type WeekStartsOn } from "./utils";

// =============================================================================

/**
 * Resolved options and callbacks shared by the views, so they are not drilled
 * through every level. The event payload type is erased here: the internals
 * only read the visual fields and hand the same objects back.
 */
export type CalendarContextValue = {
  date: Date;
  setDate: (date: Date) => void;
  locale: Locale;
  labels: CalendarLabels;
  weekStartsOn: WeekStartsOn;
  hiddenWeekdays: ReadonlySet<number>;
  minHour: number;
  maxHour: number;
  slotDuration: number;
  businessHours?: CalendarBusinessHours;
  disabledRanges: CalendarRange[];
  onSlotClick?: (slot: CalendarRange) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  renderEventDetail?: (event: CalendarEvent) => ReactNode;
  renderDayAddon?: (date: Date) => ReactNode;
  isDayDisabled?: (date: Date) => boolean;
  tone: CalendarTone;
};

export const CalendarContext = createContext<CalendarContextValue | null>(null);

/** Classes of the current day and of the active view, by tone */
export const TONE_ACTIVE: Record<CalendarTone, string> = {
  ink: "bg-foreground text-background",
  signal: "border border-signal-line bg-signal-soft text-signal",
};

export function useCalendarContext(): CalendarContextValue {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error("useCalendarContext must be used inside <Calendar />");
  }
  return context;
}
